import { useState, useEffect, useCallback } from 'react';
import { getUnansweredQuestions, getChangeObjectiveFlow } from '../config/onboardingQuestions';

/**
 * Centralized onboarding flow state management
 * Handles both full onboarding and change-objective mini-flow
 *
 * @param {Object} user - User object from auth
 * @returns {Object} Onboarding state and controls
 */
export function useOnboardingFlow(user) {
  // Persist 'completed' state in sessionStorage to survive component re-mounts.
  // This prevents the flow from restarting after plan approval if a downstream
  // component crashes and causes a remount.
  const [flowState, setFlowState] = useState(() => {
    const persisted = sessionStorage.getItem('onboarding_flow_state');
    return persisted === 'completed' ? 'completed' : 'idle';
  });
  const [flowType, setFlowType] = useState(null); // null | 'full' | 'change-objective'
  const [flowItems, setFlowItems] = useState([]);

  /**
   * Determine if onboarding should show based on user data
   */
  const checkShouldShowOnboarding = useCallback(() => {
    if (!user) return false;
    if (flowState === 'active') return true; // Already showing
    if (flowState === 'completed') return false; // Just completed

    // Check if there are unanswered questions
    const unansweredItems = getUnansweredQuestions(user);
    return unansweredItems.length > 0;
  }, [user, flowState]);

  /**
   * Start onboarding flow
   * @param {string} type - 'full' or 'change-objective'
   */
  const startOnboarding = useCallback((type = 'full') => {
    console.log('[useOnboardingFlow] Starting onboarding, type:', type);

    let items;
    if (type === 'change-objective') {
      items = getChangeObjectiveFlow();
    } else {
      items = getUnansweredQuestions(user);
    }

    setFlowType(type);
    setFlowItems(items);
    setFlowState('active');
    sessionStorage.setItem('onboarding_flow_state', 'active');
  }, [user]);

  /**
   * Complete onboarding and mark as done
   */
  const completeOnboarding = useCallback(() => {
    console.log('[useOnboardingFlow] Completing onboarding');
    setFlowState('completed');
    setFlowType(null);
    setFlowItems([]);
    sessionStorage.setItem('onboarding_flow_state', 'completed');
  }, []);

  /**
   * Reset state (for testing or logout)
   */
  const resetOnboarding = useCallback(() => {
    console.log('[useOnboardingFlow] Resetting onboarding state');
    setFlowState('idle');
    setFlowType(null);
    setFlowItems([]);
    sessionStorage.removeItem('onboarding_flow_state');
  }, []);

  /**
   * Auto-start full onboarding if needed when user loads
   */
  useEffect(() => {
    if (!user) {
      // Silently reset state when user logs out (don't log if already idle)
      if (flowState !== 'idle') {
        console.log('[useOnboardingFlow] User logged out - resetting state');
        setFlowState('idle');
        setFlowType(null);
        setFlowItems([]);
        sessionStorage.removeItem('onboarding_flow_state');
      }
      return;
    }

    // Only auto-start if idle and has unanswered questions
    if (flowState === 'idle' && checkShouldShowOnboarding()) {
      const items = getUnansweredQuestions(user);
      if (items.length > 0) {
        console.log('[useOnboardingFlow] Auto-starting onboarding for new/incomplete user');
        startOnboarding('full');
      }
    }
  }, [user, flowState, checkShouldShowOnboarding, startOnboarding]);

  return {
    // State
    shouldShowOnboarding: flowState === 'active',
    flowType,
    flowItems,
    isActive: flowState === 'active',
    isCompleted: flowState === 'completed',

    // Actions
    startOnboarding,
    completeOnboarding,
    resetOnboarding
  };
}
